import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { MOCK_MEETINGS } from '../data/mockData';

const { FiPlay, FiClock, FiUsers, FiMoreVertical, FiLoader, FiActivity } = FiIcons;

const WaveformPreview = () => (
  <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-20 group-hover:opacity-40 transition-opacity px-12">
    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
      <motion.div
        key={i}
        animate={{ height: [20, 60, 30, 80, 20] }}
        transition={{ repeat: Infinity, duration: 2, delay: i * 0.2 }}
        className="w-1 bg-cyan-400 rounded-full"
      />
    ))}
  </div>
);

export default function MeetingLibrary() {
  const navigate = useNavigate();

  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Meeting Matrix</h1>
          <p className="text-gray-400 text-sm mt-1">Unified repository for organizational intelligence.</p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-all">
            <SafeIcon icon={FiActivity} /> <span>Analytics</span>
          </button>
          <button className="px-4 py-2 bg-cyan-400 text-black font-bold rounded-lg text-sm hover:shadow-neon transition-all">
            + New Record
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-y-auto scrollbar-custom pb-20 pr-1">
        {MOCK_MEETINGS.map((meeting, index) => (
          <motion.div
            key={meeting.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-panel rounded-2xl overflow-hidden flex flex-col group cursor-pointer border border-white/5 hover:border-cyan-400/40 hover:shadow-neon-strong transition-all duration-500 h-[420px]"
            onClick={() => navigate(`/console/${meeting.id}`)}
          >
            <div className="h-44 bg-black relative overflow-hidden group">
              <img 
                src={meeting.thumbnail} 
                alt={meeting.title}
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:scale-105 group-hover:opacity-60 transition-all duration-700" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0518] via-transparent to-transparent"></div>
              <WaveformPreview />
              
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-14 h-14 rounded-full bg-cyan-400/20 backdrop-blur-md border border-cyan-400 flex items-center justify-center shadow-neon">
                  <SafeIcon icon={FiPlay} className="text-cyan-400 text-2xl ml-1" />
                </div>
              </div>

              <div className="absolute bottom-3 right-3 bg-black/80 px-2 py-1 rounded-md text-[10px] font-mono text-cyan-400 border border-cyan-400/30 backdrop-blur-sm">
                {meeting.duration}
              </div>
            </div>

            <div className="p-6 flex-1 flex flex-col">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-xl text-white group-hover:text-cyan-400 transition-colors leading-tight line-clamp-1">{meeting.title}</h3>
                <button className="text-gray-500 hover:text-white p-1" onClick={(e) => e.stopPropagation()}>
                  <SafeIcon icon={FiMoreVertical} />
                </button>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500 mb-4 font-mono">
                <div className="flex items-center gap-1.5">
                  <SafeIcon icon={FiClock} className="text-cyan-400/60" />
                  <span>{new Date(meeting.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <SafeIcon icon={FiUsers} className="text-cyan-400/60" />
                  <span>{meeting.attendees.length} Verified</span>
                </div>
              </div>

              <p className="text-sm text-gray-400 line-clamp-3 mb-6 flex-1 leading-relaxed">
                {meeting.summary}
              </p>

              <div className="flex justify-between items-center mt-auto pt-4 border-t border-white/5">
                <div className="flex -space-x-2">
                  {meeting.attendees.slice(0, 3).map((a, i) => (
                    <img 
                      key={i} 
                      src={a.avatar} 
                      alt={a.name} 
                      className="w-8 h-8 rounded-full border-2 border-[#1A0B2E] object-cover" 
                      title={a.name} 
                    />
                  ))}
                  {meeting.attendees.length > 3 && (
                    <div className="w-8 h-8 rounded-full border-2 border-[#1A0B2E] bg-violet-900 flex items-center justify-center text-[10px] text-white font-bold">
                      +{meeting.attendees.length - 3}
                    </div>
                  )}
                </div>
                <div className="flex gap-1.5">
                  {meeting.tags.slice(0, 2).map(tag => (
                    <span key={tag} className="text-[9px] px-2 py-1 rounded-md bg-cyan-400/10 text-cyan-400 border border-cyan-400/20 font-mono uppercase tracking-wider">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}